import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, ShieldCheck, Wand2 } from "lucide-react";

const inputCls = "w-full px-4 py-3 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm bg-white";

const LS_TC = "misland_last_tc";
const LS_PHONE = "misland_last_phone";
const LS_REMEMBER = "misland_remember";

export default function MemberLogin() {
  const [tc, setTc] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [remember, setRemember] = useState(() => localStorage.getItem(LS_REMEMBER) !== "0");
  const [autofilled, setAutofilled] = useState(false);
  const nav = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const t = localStorage.getItem("member_token");
    if (t) {
      axios.get(`${API}/member/me`, { headers: { Authorization: `Bearer ${t}` } })
        .then(() => nav("/uye", { replace: true }))
        .catch(() => {});
    }
    // Autofill from URL first, then from localStorage
    const params = new URLSearchParams(location.search);
    const urlTc = params.get("tc") || "";
    const urlPhone = params.get("phone") || "";
    const savedTc = localStorage.getItem(LS_TC) || "";
    const savedPhone = localStorage.getItem(LS_PHONE) || "";
    const nextTc = urlTc || savedTc;
    const nextPhone = urlPhone || savedPhone;
    if (nextTc) setTc(nextTc.replace(/\D/g, "").slice(0, 11));
    if (nextPhone) setPhone(nextPhone);
    if (nextTc || nextPhone) setAutofilled(true);
  }, []);

  const clearAutofill = () => {
    setTc(""); setPhone(""); setAutofilled(false);
    localStorage.removeItem(LS_TC); localStorage.removeItem(LS_PHONE);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (tc.length !== 11) { toast.error("T.C. kimlik 11 hane olmalıdır"); return; }
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 7) { toast.error("Geçerli bir telefon numarası girin"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/member/login`, { tc_kimlik: tc.trim(), phone: digits }, { withCredentials: true });
      localStorage.setItem("member_token", r.data.token);
      // Save credentials for QR/auto-fill next time if user opted in
      if (remember) {
        localStorage.setItem(LS_TC, tc.trim());
        localStorage.setItem(LS_PHONE, digits);
        localStorage.setItem(LS_REMEMBER, "1");
      } else {
        localStorage.removeItem(LS_TC);
        localStorage.removeItem(LS_PHONE);
        localStorage.setItem(LS_REMEMBER, "0");
      }
      toast.success(`Hoş geldiniz, ${r.data.member.ad_soyad}`);
      nav("/uye", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Giriş başarısız");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="hidden lg:block relative overflow-hidden">
        <img src="https://customer-assets-0z36b82j.emergentagent.net/job_partner-registry-2/artifacts/yp5b7tl5_yn4.webp"
             alt="Kaya Şehir Misland — Giriş"
             className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/70 via-slate-900/50 to-slate-900/80" />
        <div className="relative z-10 p-12 h-full flex flex-col justify-between text-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-white/15 backdrop-blur flex items-center justify-center">
              <ShieldCheck size={20} />
            </div>
            <span className="font-semibold tracking-tight text-lg">Ortak Portalı</span>
          </div>
          <div>
            <h1 className="text-4xl sm:text-5xl font-extrabold leading-[1.05] mb-4">
              Hisse bilgilerinize<br/>hızlı ve güvenli erişim.
            </h1>
            <p className="text-white/80 max-w-md text-sm">
              T.C. kimlik ve kayıtlı telefon numaranız ile şifresiz giriş yapın; hisselerinizi görün, iletişim bilgilerinizi güncelleyin, ortaklık belgenizi indirin.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <form onSubmit={submit} className="w-full max-w-md">
          <div className="mb-10 space-y-5">
            <img src="https://customer-assets-0z36b82j.emergentagent.net/job_partner-registry-2/artifacts/4mwtidfk_misas.png" alt="MİSAŞ" className="h-9 object-contain" />
            <img src="https://customer-assets-0z36b82j.emergentagent.net/job_partner-registry-2/artifacts/4d24m9nu_misholding.webp" alt="MİS HOLDİNG" className="h-9 object-contain" />
            <img src="https://customer-assets-0z36b82j.emergentagent.net/job_partner-registry-2/artifacts/azkn7y74_miskoop.png" alt="MİS KOOP" className="h-9 object-contain" />
          </div>
          <div className="mb-8">
            <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-3">Ortak Girişi</div>
            <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight mb-2">Hisse hesabıma giriş</h2>
            <p className="text-slate-600 text-sm">T.C. kimlik numaranız ve kayıtlı telefon numaranız yeterli — şifre gerekmez.</p>
          </div>

          {autofilled && (
            <div className="mb-4 flex items-start justify-between gap-3 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2.5" data-testid="mlogin-autofilled-banner">
              <div className="flex items-start gap-2">
                <Wand2 size={14} className="mt-0.5 text-emerald-700"/>
                <div className="text-[12px] text-emerald-900 leading-relaxed">
                  Bilgileriniz cihazınızdan otomatik dolduruldu. <span className="font-semibold">"Giriş Yap"</span> ile devam edin.
                </div>
              </div>
              <button type="button" onClick={clearAutofill}
                className="text-[11px] font-semibold text-emerald-800 underline underline-offset-2 hover:no-underline whitespace-nowrap">
                Temizle
              </button>
            </div>
          )}

          <div className="space-y-4">
            <label className="block">
              <span className="block text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5">T.C. Kimlik Numarası</span>
              <input data-testid="mlogin-tc" inputMode="numeric" maxLength={11} className={inputCls} value={tc} onChange={e => setTc(e.target.value.replace(/\D/g, ""))} placeholder="11 haneli T.C. kimlik" />
            </label>
            <label className="block">
              <span className="block text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5">Kayıtlı Telefon Numaranız</span>
              <input data-testid="mlogin-phone" inputMode="tel" className={inputCls} value={phone} onChange={e => setPhone(e.target.value)} placeholder="Örn: 5xx xxx xx xx" />
              <span className="block mt-1.5 text-[11px] text-slate-500">Sistemde kayıtlı 4 telefon alanından (Tel 1/2, GSM 1/2) herhangi biri ile eşleşme yeterlidir.</span>
            </label>
          </div>

          <label className="mt-4 flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)}
                   data-testid="mlogin-remember" className="h-4 w-4 accent-slate-900" />
            <span className="text-xs text-slate-700">Bu cihazda beni hatırla — bir dahaki girişte otomatik doldur.</span>
          </label>

          <button data-testid="mlogin-submit" disabled={busy} type="submit"
            className="mt-5 w-full inline-flex items-center justify-center gap-3 rounded-md bg-slate-900 text-white font-semibold py-3.5 hover:bg-slate-800 disabled:opacity-60">
            <LogIn size={18} /> {busy ? "Giriş yapılıyor..." : "Giriş Yap"}
          </button>

          <div className="mt-6 text-sm text-slate-600 space-y-1.5">
            <div className="text-xs text-slate-500">Telefonunuz kayıtlarımızda güncel değilse yönetimle iletişime geçebilirsiniz.</div>
            <div className="text-xs text-slate-500">Yönetici misiniz? <Link to="/login" className="underline underline-offset-2 text-slate-800 font-semibold">Yönetim girişi</Link>.</div>
          </div>
        </form>
      </div>
    </div>
  );
}
